import React, { useCallback, useState, useRef, useEffect } from "react";
import { Container, Graphics, Text } from "@pixi/react";
import { CornerNode } from "../nodes";
import * as PIXI from "pixi.js";
import "pixi.js/events";
import { useApp } from "../../context/AppContext";
import { findNearbyEndpoints } from "../../utils/shapeDetection";
import { createShape } from "../../utils/shapeFactory";

const EditableLine = ({
  shape,
  isSelected,
  onUpdate,
  onSelect,
  stageRef,
  allShapes = [],
  isPartOfRectangle = false,
  rectangleIndex = null,
  onRectangleNodeMouseDown = null,
  selectedMeasurementUnit = {
    id: 'meters',
    name: 'Meters',
    symbol: 'm',
    abbreviation: 'm',
    conversionFactor: 1,
  },
  isBlueprintMode = false,
  isSharedLine = false,
  onDelete = null
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isScaling, setIsScaling] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [initialShape, setInitialShape] = useState(null);
  const [activeNodeIndex, setActiveNodeIndex] = useState(null);
  const [isCurveMode, setIsCurveMode] = useState(false);
  const [controlPoint, setControlPoint] = useState(null);
  const [isDraggingControl, setIsDraggingControl] = useState(false);
  const containerRef = useRef();
  const isNodeClicked = useRef(false);

  // Get app context for connection management
  const context = useApp();
  const { connections = [], addConnection, removeConnection, addClosedShape, removeClosedShape, clearAllClosedShapes } = context;
  
  console.log('🔍 EditableLine context debug:', {
    contextKeys: Object.keys(context),
    hasAddConnection: typeof addConnection === 'function',
    addConnectionType: typeof addConnection,
    addConnectionValue: addConnection,
    hasClearAllClosedShapes: typeof clearAllClosedShapes === 'function',
    clearAllClosedShapesType: typeof clearAllClosedShapes,
    clearAllClosedShapesValue: clearAllClosedShapes,
    connectionsType: typeof connections,
    connectionsValue: connections
  });

  // Debug logging for shapes array
  useEffect(() => {
    console.log('🔍 EditableLine shapes context:', {
      currentShapeId: shape?.id,
      shapesArray: allShapes,
      shapesLength: allShapes?.length,
      connectionsArray: connections || [],
      connectionsLength: (connections || []).length
    });
  }, [allShapes, connections, shape?.id]);

  // Initialize curve state from shape's existing curve data
  useEffect(() => {
    if (shape) {
      const hasExistingCurve = shape.isCurveMode && shape.controlPoint;
      if (hasExistingCurve) {
        setIsCurveMode(shape.isCurveMode);
        setControlPoint(shape.controlPoint);
      } else {
        // Reset curve state if no curve data exists
        setIsCurveMode(false);
        setControlPoint(null);
      }
    }
  }, [shape.id, shape.isCurveMode, shape.controlPoint, rectangleIndex]);

  // Initialize or update control points
  const initializeControlPoints = useCallback(() => {
    const startNode = shape.nodes[0];
    const endNode = shape.nodes[shape.nodes.length - 1];
    
    // Calculate midpoint between start and end
    const midPoint = {
      x: (startNode.x + endNode.x) / 2,
      y: (startNode.y + endNode.y) / 2
    };
    
    // Calculate angle of the line
    const lineAngle = Math.atan2(endNode.y - startNode.y, endNode.x - startNode.x);
    
    // Set control point perpendicular to the line (30 pixels away)
    return {
      start: {
        x: midPoint.x + Math.sin(lineAngle) * 30,
        y: midPoint.y - Math.cos(lineAngle) * 30
      }
    };
  }, [shape]);

  // Listen for popup responses
  useEffect(() => {
    const handlePopupResponse = (event) => {
      const { action, confirmed, shapeId, lineIndex } = event.detail;
      
      // Only handle events for this specific shape/line
      if (shapeId !== shape.id || lineIndex !== rectangleIndex) return;
      
      if (!confirmed) return; // User cancelled
      
      if (action === 'activate') {
        setIsCurveMode(true);
        const newControlPoints = initializeControlPoints();
        setControlPoint(newControlPoints.start);
        
        // Save curve state in shape data - send only curve properties for Rectangle to save to lineData
        onUpdate({
          isCurveMode: true,
          controlPoint: newControlPoints.start
        });
      } else if (action === 'exit') {
        // Keep the original line structure but position middle node to match the curve
        const finalNodes = [...shape.nodes];
        const midIndex = Math.floor(shape.nodes.length / 2);
        
        if (controlPoint) {
          const p0 = shape.nodes[0];
          const p1 = controlPoint;
          const p3 = shape.nodes[shape.nodes.length - 1];
          
          // Calculate the position of the middle node at the midpoint of the curve (t = 0.5)
          const t = 0.5;
          const x = Math.pow(1-t, 3) * p0.x + 
                   3 * Math.pow(1-t, 2) * t * p1.x + 
                   3 * (1-t) * Math.pow(t, 2) * p1.x + 
                   Math.pow(t, 3) * p3.x;
          const y = Math.pow(1-t, 3) * p0.y + 
                   3 * Math.pow(1-t, 2) * t * p1.y + 
                   3 * (1-t) * Math.pow(t, 2) * p1.y + 
                   Math.pow(t, 3) * p3.y;
          
          // Update the middle node to match the curve position
          finalNodes[midIndex] = { x, y };
        }
        
        setIsCurveMode(false);
        setControlPoint(null);
        
        // Update shape with the adjusted middle node and remove curve state - send only curve properties
        onUpdate({
          isCurveMode: false,
          controlPoint: null,
          nodes: finalNodes
        });
      }
    };

    window.addEventListener('curvePopupResponse', handlePopupResponse);
    return () => window.removeEventListener('curvePopupResponse', handlePopupResponse);
  }, [shape, isCurveMode, initializeControlPoints, onUpdate, controlPoint, rectangleIndex]);

  const handleControlPointMouseDown = useCallback((event, point) => {
    event.stopPropagation();
    setIsDraggingControl(true);
    
    // Get the global coordinates from PIXI event
    const globalPoint = event.data?.global || event;
    setDragStart({
      x: globalPoint.x,
      y: globalPoint.y
    });
  }, []);

  const handleControlPointMouseMove = useCallback((event) => {
    if (!isDraggingControl || !dragStart) return;

    // Get global coordinates from PIXI event
    const globalPoint = event.data?.global || event;
    
    // Convert to local coordinates relative to the line's position
    const localPoint = {
      x: globalPoint.x - shape.x,
      y: globalPoint.y - shape.y
    };

    // Update local state immediately
    setControlPoint(localPoint);

    // Update shape with new control point - ensure curve mode stays active
    onUpdate({
      isCurveMode: true, // Explicitly maintain curve mode
      controlPoint: localPoint
    });
  }, [isDraggingControl, dragStart, shape, onUpdate, isCurveMode, controlPoint]);

  const handleControlPointMouseUp = useCallback(() => {
    setIsDraggingControl(false);
    setDragStart(null);
  }, [shape.id, controlPoint]);

  // Add global mouse event listeners for control point dragging
  useEffect(() => {
    if (isDraggingControl) {
      const handleGlobalMouseMove = (event) => {
        if (!dragStart) return;
        
        // Get coordinates from the canvas
        const stage = stageRef.current;
        if (!stage) return;

        const canvas = stage._canvas;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
        const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;

        const globalPoint = {
          x: (event.clientX - rect.left) * scaleX,
          y: (event.clientY - rect.top) * scaleY
        };

        // Convert to local coordinates relative to the line's position
        const localPoint = {
          x: globalPoint.x - shape.x,
          y: globalPoint.y - shape.y
        };

        // Update local state immediately
        setControlPoint(localPoint);

        // Update shape with new control point - explicitly maintain curve mode
        onUpdate({
          isCurveMode: true, // Explicitly maintain curve mode
          controlPoint: localPoint
        });
      };

      const handleGlobalMouseUp = () => {
        setIsDraggingControl(false);
        setDragStart(null);
      };

      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDraggingControl, dragStart, shape, onUpdate, stageRef, isCurveMode, controlPoint]);

  const handleMouseDown = useCallback(
    (event) => {
      console.log('🔥 EditableLine handleMouseDown (line selection):', {
        shapeId: shape?.id,
        isPartOfRectangle,
        currentlySelected: isSelected
      });
      
      if (!shape) return;
      
      event.stopPropagation();
      onSelect();

      let globalPoint;
      if (event.data?.global) {
        globalPoint = event.data.global;
      }

      setIsDragging(true);
      setDragStart({
        x: globalPoint.x - shape.x,
        y: globalPoint.y - shape.y,
      });
      window.dispatchEvent(
        new CustomEvent("propogation-block", { detail: { isBlocked: false } })
      );
    },
    [shape, onSelect, isPartOfRectangle, isSelected]
  );

  // Helper function to determine if a node is at a rectangle corner
  const isRectangleCornerNode = useCallback((nodeIndex) => {
    if (!isPartOfRectangle || rectangleIndex === null) return false;
    
    // Corner nodes are at specific positions based on rectangle line index and node position
    const cornerMap = {
      // Top-left corner
      0: nodeIndex === 0, // top1 line start node
      7: nodeIndex === 0, // left2 line start node
      
      // Top-right corner  
      1: nodeIndex === 1, // top2 line end node
      2: nodeIndex === 0, // right1 line start node
      
      // Bottom-right corner
      3: nodeIndex === 1, // right2 line end node
      4: nodeIndex === 0, // bottom1 line start node
      
      // Bottom-left corner
      5: nodeIndex === 1, // bottom2 line end node
      6: nodeIndex === 0  // left1 line start node
    };
    
    return cornerMap[rectangleIndex] || false;
  }, [isPartOfRectangle, rectangleIndex]);

  // Helper function to get the rectangle corner index for a node
  const getRectangleCornerIndex = useCallback((nodeIndex) => {
    if (!isRectangleCornerNode(nodeIndex)) return null;
    
    const cornerIndexMap = {
      0: 0, // top1 start -> top-left (0)
      1: 1, // top2 end -> top-right (1)  
      2: 1, // right1 start -> top-right (1)
      3: 2, // right2 end -> bottom-right (2)
      4: 2, // bottom1 start -> bottom-right (2)
      5: 3, // bottom2 end -> bottom-left (3)
      6: 3, // left1 start -> bottom-left (3)
      7: 0  // left2 start -> top-left (0)
    };
    
    return cornerIndexMap[rectangleIndex] || null;
  }, [rectangleIndex, isRectangleCornerNode]);

  const handleNodeMouseDown = useCallback(
    (event, nodeType, index, globalPoint) => {
      console.log('🎯 EditableLine handleNodeMouseDown called:', {
        nodeType,
        index,
        isPartOfRectangle,
        rectangleIndex,
        shapeId: shape?.id,
        totalNodes: shape?.nodes?.length
      });
      
      if (!shape) return;
      event.stopPropagation();
      isNodeClicked.current = true;

      if (isPartOfRectangle) {
        // Check if this is a corner node that should trigger rectangle scaling
        const isCornerNode = isRectangleCornerNode(index);
        
        if (isCornerNode && onRectangleNodeMouseDown) {
          // This is a corner node - delegate to rectangle scaling
          const cornerIndex = getRectangleCornerIndex(index);
          console.log('🏠 Delegating to rectangle corner scaling:', {
            lineIndex: rectangleIndex,
            nodeIndex: index,
            cornerIndex
          });
          
          onRectangleNodeMouseDown(event, 'corner', cornerIndex, globalPoint);
          return;
        }
        
        // For non-corner rectangle line nodes, allow individual line scaling
        if (nodeType === 'corner' && (index === 0 || index === shape.nodes.length - 1)) {
          console.log('✅ Starting individual line scaling for rectangle line node:', index);
          setIsScaling(true);
          setActiveNodeIndex(index);
          setInitialShape({ ...shape });
          setDragStart(globalPoint);
        } else {
          console.log('❌ Node not scalable for rectangle:', { nodeType, index, totalNodes: shape.nodes.length });
        }
      } else {
        // For standalone lines, allow scaling of end nodes and middle nodes
        if (nodeType === 'corner' && (index === 0 || index === shape.nodes.length - 1)) {
          console.log('✅ Starting scaling for standalone line end node:', index);
          setIsScaling(true);
          setActiveNodeIndex(index);
          setInitialShape({ ...shape });
          setDragStart(globalPoint);
        }
        // For middle nodes (side nodes), also allow scaling
        else if (nodeType === 'corner' && index > 0 && index < shape.nodes.length - 1) {
          console.log('✅ Starting scaling for standalone line middle node:', index);
          setIsScaling(true);
          setActiveNodeIndex(index);
          setInitialShape({ ...shape });
          setDragStart(globalPoint);
        }
      }

      window.dispatchEvent(
        new CustomEvent("propogation-block", { detail: { isBlocked: true } })
      );
    },
    [shape, isPartOfRectangle, rectangleIndex, isRectangleCornerNode, getRectangleCornerIndex, onRectangleNodeMouseDown]
  );

  const handleNodeMouseUp = useCallback(() => {
    setIsScaling(false);
    setActiveNodeIndex(null);
    setDragStart(null);
    setInitialShape(null);
  }, []);

  // Function to handle endpoint snapping and connections
  const handleEndpointSnapping = useCallback((shapeId, nodeIndex, newPosition) => {
    // Safety check for shapes array
    if (!allShapes || !Array.isArray(allShapes)) {
      console.warn('handleEndpointSnapping: shapes array is not available:', allShapes);
      return newPosition;
    }

    // Debug logging
    console.log('🔍 handleEndpointSnapping called:', {
      shapeId,
      nodeIndex,
      newPosition,
      shapesArray: allShapes,
      shapesLength: allShapes?.length,
      connectionsArray: connections || [],
      connectionsLength: (connections || []).length
    });

    // Find nearby endpoints
    const nearbyEndpoint = findNearbyEndpoints(
      { id: shapeId }, 
      nodeIndex, 
      allShapes
    );

    console.log('🔍 nearbyEndpoint result:', nearbyEndpoint);

    if (nearbyEndpoint) {
      console.log('🔗 Found nearby endpoint, attempting to snap:', nearbyEndpoint);
      
      // Snap to the nearby endpoint
      const targetShape = allShapes.find(s => s.id === nearbyEndpoint.shapeId);
      if (targetShape) {
        console.log('🎯 Target shape found:', targetShape);
        
        const targetNode = targetShape.nodes[nearbyEndpoint.nodeIndex];
        const snappedPosition = {
          x: targetShape.x + targetNode.x - shape.x,
          y: targetShape.y + targetNode.y - shape.y
        };

        console.log('📍 Snapped position calculated:', snappedPosition);

        // Add connection
        console.log('🔗 Adding connection:', {
          shapeId1: shapeId,
          nodeIndex1: nodeIndex,
          shapeId2: nearbyEndpoint.shapeId,
          nodeIndex2: nearbyEndpoint.nodeIndex
        });
        
        if (typeof addConnection === 'function') {
          addConnection(shapeId, nodeIndex, nearbyEndpoint.shapeId, nearbyEndpoint.nodeIndex);
        } else {
          console.error('❌ addConnection is not a function:', addConnection);
        }

        // Update the node position to snap
        const updatedNodes = [...shape.nodes];
        updatedNodes[nodeIndex] = snappedPosition;
        
        console.log('📝 Updating nodes:', updatedNodes);
        onUpdate({ nodes: updatedNodes });

        // Trigger shape detection
        setTimeout(() => {
          console.log('🔍 Triggering shape detection...');
          // Import and call detection function directly to avoid circular dependency
          import('../../utils/shapeDetection').then(({ detectClosedShapes, calculateShapeVertices, generateShapeColor }) => {
            console.log('🔍 Shape detection functions imported');
            const cycles = detectClosedShapes(allShapes, connections || []);
            console.log('🔍 Detected cycles:', cycles);
            
            // Clear all existing closed shapes first
            if (typeof clearAllClosedShapes === 'function') {
              clearAllClosedShapes();
            } else {
              console.error('❌ clearAllClosedShapes is not a function:', clearAllClosedShapes);
            }
            
            // Add new closed shapes
            cycles.forEach((cycle, index) => {
              const lineIds = cycle.map(node => node.shapeId);
              const vertices = calculateShapeVertices(cycle, allShapes);
              const fillColor = generateShapeColor(index);
              
              console.log('🎨 Creating closed shape:', {
                lineIds,
                vertices,
                fillColor,
                cycle
              });
              
              if (typeof addClosedShape === 'function') {
                addClosedShape(lineIds, vertices, fillColor);
              } else {
                console.error('❌ addClosedShape is not a function:', addClosedShape);
              }

              // Create a new polygonRoom shape and add it to the main shapes array
              if (typeof context.addShape === 'function') {
                const polygonRoom = createShape('polygonRoom', 0, 0, 0, 0, { vertices });
                context.addShape(polygonRoom);
                // Auto-select the new polygonRoom
                if (typeof context.selectShape === 'function') {
                  context.selectShape(polygonRoom.id);
                }
                // Show a notification
                window.dispatchEvent(new CustomEvent('roomDetected', { detail: { id: polygonRoom.id } }));
                console.log('🏠 Added and selected polygonRoom shape:', polygonRoom);
              } else {
                console.error('❌ addShape is not a function:', context.addShape);
              }
            });
          });
        }, 100);

        return snappedPosition;
      } else {
        console.warn('❌ Target shape not found for ID:', nearbyEndpoint.shapeId);
      }
    } else {
      console.log('🔍 No nearby endpoint found');
    }

    // Remove any existing connections for this node if not snapped
    if (!nearbyEndpoint && connections && Array.isArray(connections)) {
      connections.forEach(connection => {
        if ((connection.shapeId1 === shapeId && connection.nodeIndex1 === nodeIndex) ||
            (connection.shapeId2 === shapeId && connection.nodeIndex2 === nodeIndex)) {
          if (typeof removeConnection === 'function') {
            removeConnection(connection.shapeId1, connection.nodeIndex1, connection.shapeId2, connection.nodeIndex2);
          } else {
            console.error('❌ removeConnection is not a function:', removeConnection);
          }
        }
      });
    }

    // After any connection add/remove, trigger closed shape detection
    setTimeout(() => {
      import('../../utils/shapeDetection').then(({ detectClosedShapes, calculateShapeVertices, generateShapeColor }) => {
        const cycles = detectClosedShapes(allShapes, connections || []);
        if (typeof clearAllClosedShapes === 'function') {
          clearAllClosedShapes();
        }
        cycles.forEach((cycle, index) => {
          const lineIds = cycle.map(node => node.shapeId);
          const vertices = calculateShapeVertices(cycle, allShapes);
          const fillColor = generateShapeColor(index);
          if (typeof addClosedShape === 'function') {
            addClosedShape(lineIds, vertices, fillColor);
          }
          if (typeof context.addShape === 'function') {
            const polygonRoom = createShape('polygonRoom', 0, 0, 0, 0, { vertices });
            context.addShape(polygonRoom);
          }
        });
      });
    }, 100);

    return newPosition;
  }, [allShapes, connections, addConnection, removeConnection, shape, onUpdate, clearAllClosedShapes, addClosedShape, context]);

  const handleNodeMouseMove = useCallback(
    (event, globalPoint) => {
      if (!shape || !initialShape) return;

      // Handle scaling for all node types
      if (isScaling && activeNodeIndex !== null && dragStart) {
        const isStart = activeNodeIndex === 0;
        const isEnd = activeNodeIndex === shape.nodes.length - 1;

        if (isPartOfRectangle) {
          // Handle end node scaling for rectangle lines (2 nodes only)
          // Simply update the dragged node position, let Rectangle.js handle the rectangle scaling
          const newNodes = [...initialShape.nodes];
          
          // Update the dragged node position
          newNodes[activeNodeIndex] = {
            x: globalPoint.x - shape.x,
            y: globalPoint.y - shape.y
          };

          const updates = { nodes: newNodes };
          onUpdate(updates);
        } else {
          // Handle end node scaling for standalone lines (existing logic)
          const fixedNodeIndex = isStart ? shape.nodes.length - 1 : 0;
          const movingNodeIndex = isStart ? 0 : shape.nodes.length - 1;

          // Calculate new positions for all nodes
          const newNodes = initialShape.nodes.map((node, i) => {
            if (i === fixedNodeIndex) {
              // Keep fixed node in place
              return node;
            }
            
            if (i === movingNodeIndex) {
              // Moving node follows the mouse
              const newPosition = {
                x: globalPoint.x - shape.x,
                y: globalPoint.y - shape.y
              };

              // Apply endpoint snapping for standalone lines
              if (isStart || isEnd) {
                return handleEndpointSnapping(shape.id, i, newPosition);
              }

              return newPosition;
            }

            // For intermediate nodes, interpolate based on position
            const t = i / (shape.nodes.length - 1);
            const fixedGlobal = {
              x: shape.x + initialShape.nodes[fixedNodeIndex].x,
              y: shape.y + initialShape.nodes[fixedNodeIndex].y
            };
            
            // Calculate the new position along the line
            const newX = fixedGlobal.x + (globalPoint.x - fixedGlobal.x) * t - shape.x;
            const newY = fixedGlobal.y + (globalPoint.y - fixedGlobal.y) * t - shape.y;

            return { x: newX, y: newY };
          });

          const updates = { nodes: newNodes };
          onUpdate(updates);
        }
      }
    },
    [shape, initialShape, isScaling, activeNodeIndex, dragStart, onUpdate, isPartOfRectangle, handleEndpointSnapping]
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (!isDragging || !dragStart) return;

      let globalPoint;
      if (event.data?.global) {
        globalPoint = event.data.global;
      }

      const newX = globalPoint.x - dragStart.x;
      const newY = globalPoint.y - dragStart.y;

      onUpdate({ x: newX, y: newY });
    },
    [isDragging, dragStart, onUpdate]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (event) => {
        if (!dragStart) return;

        const stage = stageRef.current;
        if (!stage) return;

        const canvas = stage._canvas;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
        const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;

        const globalPoint = {
          x: (event.clientX - rect.left) * scaleX,
          y: (event.clientY - rect.top) * scaleY
        };

        const newX = globalPoint.x - dragStart.x;
        const newY = globalPoint.y - dragStart.y;

        onUpdate({ x: newX, y: newY });
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);
        setDragStart(null);
      };

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart, onUpdate, stageRef]);

  // Add global mouse move handler for continuous scaling
  useEffect(() => {
    if ((isScaling || isDragging) && dragStart) {
      const handleGlobalMouseMove = (event) => {
        const stage = stageRef.current;
        if (!stage) return;

        const canvas = stage._canvas;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
        const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;

        const globalPoint = {
          x: (event.clientX - rect.left) * scaleX,
          y: (event.clientY - rect.top) * scaleY
        };

        if (isScaling || isDragging) {
          handleNodeMouseMove(event, globalPoint);
        } else if (isDragging) {
          handleMouseMove({ data: { global: globalPoint } });
        }
      };

      const handleGlobalMouseUp = () => {
        setIsScaling(false);
        setIsDragging(false);
        setDragStart(null);
        setInitialShape(null);
      };

      window.addEventListener("mousemove", handleGlobalMouseMove);
      window.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        window.removeEventListener("mousemove", handleGlobalMouseMove);
        window.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isScaling, isDragging, dragStart, handleNodeMouseMove, handleMouseMove, stageRef]);

  // Calculate line lengths
  const calculateLineLengths = useCallback(() => {
    if (!shape || !shape.nodes || shape.nodes.length < 2) return { total: 0 };

    const startNode = shape.nodes[0];
    const endNode = shape.nodes[shape.nodes.length - 1];

    // Convert pixels to selected unit (assuming 100 pixels = 1 meter)
    const pixelsToSelectedUnit = (pixels) => {
      const meters = pixels / 100;
      const convertedValue = meters * (selectedMeasurementUnit?.conversionFactor || 3.28084);
      return convertedValue.toFixed(1);
    };

    let totalLength;

    if (isCurveMode && controlPoint) {
      // Calculate curved line length using quadratic curve approximation
      // We'll use multiple points along the curve to approximate the length
      const segments = 10;
      let curveLength = 0;
      
      for (let i = 0; i < segments; i++) {
        const t1 = i / segments;
        const t2 = (i + 1) / segments;
        
        // Calculate points on quadratic curve using parametric equations
        const p1 = {
          x: Math.pow(1-t1, 2) * startNode.x + 2 * (1-t1) * t1 * controlPoint.x + Math.pow(t1, 2) * endNode.x,
          y: Math.pow(1-t1, 2) * startNode.y + 2 * (1-t1) * t1 * controlPoint.y + Math.pow(t1, 2) * endNode.y
        };
        
        const p2 = {
          x: Math.pow(1-t2, 2) * startNode.x + 2 * (1-t2) * t2 * controlPoint.x + Math.pow(t2, 2) * endNode.x,
          y: Math.pow(1-t2, 2) * startNode.y + 2 * (1-t2) * t2 * controlPoint.y + Math.pow(t2, 2) * endNode.y
        };
        
        // Add distance between consecutive points
        curveLength += Math.sqrt(
          Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)
        );
      }
      
      totalLength = curveLength;
    } else {
      // Calculate straight line distance
      totalLength = Math.sqrt(
        Math.pow(endNode.x - startNode.x, 2) + 
        Math.pow(endNode.y - startNode.y, 2)
      );
    }

    return { 
      total: pixelsToSelectedUnit(totalLength)
    };
  }, [shape, isCurveMode, controlPoint, selectedMeasurementUnit]);

  const drawCurvedLine = useCallback(
    (g) => {
      if (!shape || !shape.nodes || !controlPoint) return;

      // Draw quadratic bezier curve
      g.lineStyle(2, isSelected ? 0x4a90e2 : 0x666666);
      
      const p0 = shape.nodes[0];
      const p1 = controlPoint;
      const p2 = shape.nodes[shape.nodes.length - 1];

      // Draw the curve using quadratic bezier
      g.moveTo(p0.x, p0.y);
      g.quadraticCurveTo(p1.x, p1.y, p2.x, p2.y);
    },
    [shape, isSelected, calculateLineLengths, isCurveMode, controlPoint, isPartOfRectangle]
  );

  // Separate component for control point interaction
  const drawControlPoint = useCallback(
    (g) => {
      if (!controlPoint) return;
      
      g.clear();
      g.interactive = true;
      g.cursor = 'pointer';
      
      // Draw control point with larger hit area
      g.lineStyle(0);
      g.beginFill(0xFF0000, 0.01); // Invisible hit area
      g.drawCircle(0, 0, 12);
      g.endFill();
      
      // Draw visible control point
      g.lineStyle(1, 0xFF0000);
      g.beginFill(0xFF0000, 0.8);
      g.drawCircle(0, 0, 5);
      g.endFill();
    },
    [controlPoint]
  );

  // Separate component for control lines
  const drawControlLines = useCallback(
    (g) => {
      if (!controlPoint) return;
      
      g.clear();
      
      // Draw lines from control point to start and end
      g.lineStyle(1, 0xFF0000, 0.5);
      g.moveTo(shape.nodes[0].x, shape.nodes[0].y);
      g.lineTo(controlPoint.x, controlPoint.y);
      g.moveTo(controlPoint.x, controlPoint.y);
      g.lineTo(shape.nodes[shape.nodes.length - 1].x, shape.nodes[shape.nodes.length - 1].y);
    },
    [shape, controlPoint]
  );

  const drawLine = useCallback(
    (g) => {
      if (!shape || !shape.nodes || shape.nodes.length < 2) return;

      g.clear();

      // Make sure graphics is interactive for line grabbing
      g.interactive = true;
      g.cursor = 'pointer';

      // Draw hit area rectangle first
      g.lineStyle(0);
      // Use shape alpha for preview mode, default to very low alpha for hit detection
      const hitAlpha = shape.isPreview ? 0 : 0.01; // No hit area for preview
      g.beginFill(0x000000, hitAlpha);
      
      // Calculate the bounds of the line
      const lineWidth = 20; // Width of hit area
      
      // For each segment of the line
      for (let i = 0; i < shape.nodes.length - 1; i++) {
        const start = shape.nodes[i];
        const end = shape.nodes[i + 1];
        
        // Calculate angle of the line segment
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        
        // Calculate the corners of the rectangle for this segment
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        
        // Calculate four corners of the rectangle
        const x1 = start.x - (lineWidth/2) * sin;
        const y1 = start.y + (lineWidth/2) * cos;
        const x2 = start.x + (lineWidth/2) * sin;
        const y2 = start.y - (lineWidth/2) * cos;
        const x3 = end.x + (lineWidth/2) * sin;
        const y3 = end.y - (lineWidth/2) * cos;
        const x4 = end.x - (lineWidth/2) * sin;
        const y4 = end.y + (lineWidth/2) * cos;
        
        // Draw the rectangle
        g.moveTo(x1, y1);
        g.lineTo(x2, y2);
        g.lineTo(x3, y3);
        g.lineTo(x4, y4);
        g.lineTo(x1, y1);
      }
      g.endFill();

      // Draw the line (either curved or straight)
      if (isCurveMode && controlPoint) {
        // Draw quadratic bezier curve
        const lineAlpha = shape.isPreview ? (shape.alpha || 0.5) : 1;
        const defaultColor = isBlueprintMode ? 0xffffff : (isSelected ? 0x4a90e2 : 0x666666);
        const lineColor = isBlueprintMode ? 0xffffff : (shape.strokeColor || defaultColor);
        const lineWidthValue = shape.strokeWidth || 1;
        g.lineStyle(lineWidthValue, lineColor, lineAlpha);
        const p0 = shape.nodes[0];
        const p1 = controlPoint;
        const p2 = shape.nodes[shape.nodes.length - 1];
        g.moveTo(p0.x, p0.y);
        g.quadraticCurveTo(p1.x, p1.y, p2.x, p2.y);
      } else {
        // Draw straight line
        const lineAlpha = shape.isPreview ? (shape.alpha || 0.5) : 1;
        const defaultColor = isBlueprintMode ? 0xffffff : (isSelected ? 0x4a90e2 : 0x666666);
        const lineColor = isBlueprintMode ? 0xffffff : (shape.strokeColor || defaultColor);
        const lineWidthValue = shape.strokeWidth || 1;
        g.lineStyle(lineWidthValue, lineColor, lineAlpha);
        g.moveTo(shape.nodes[0].x, shape.nodes[0].y);
        for (let i = 1; i < shape.nodes.length; i++) {
          g.lineTo(shape.nodes[i].x, shape.nodes[i].y);
        }
      }

      // Only draw measurements if not disabled
      if (shape.showMeasurement !== false) {
        const { total } = calculateLineLengths();
        
        // Calculate midpoint for measurement placement
        const midX = (shape.nodes[0].x + shape.nodes[shape.nodes.length - 1].x) / 2;
        const midY = (shape.nodes[0].y + shape.nodes[shape.nodes.length - 1].y) / 2;
        
        // Calculate angle for positioning measurement above the line
        const lineAngle = Math.atan2(
          shape.nodes[shape.nodes.length - 1].y - shape.nodes[0].y,
          shape.nodes[shape.nodes.length - 1].x - shape.nodes[0].x
        );
        
        // Position the measurement further outside when part of rectangle
        const offset = isPartOfRectangle ? 25 : 15;
        const labelX = midX - Math.sin(lineAngle) * offset;
        const labelY = midY - Math.cos(lineAngle) * offset;

        // Store measurement position for text rendering
        g.measurementData = {
          text: `${total}${selectedMeasurementUnit?.abbreviation || 'm'}${isCurveMode ? ' (curved)' : ''}${shape.isGapFiller ? ' (gap)' : ''}`,
          x: labelX - 15,
          y: labelY - 4
        };
      }
    },
    [shape, isSelected, calculateLineLengths, isCurveMode, controlPoint, isPartOfRectangle]
  );

  // Handle curve activation with "C" key instead of right-click
  const handleCurveKeyPress = useCallback((event) => {
    // Only handle if this line is selected and "C" key is pressed
    if (!isSelected || event.key.toLowerCase() !== 'c') return;
    
    event.preventDefault();
    event.stopPropagation();
    
    // Always dispatch the standard curve popup
    const action = isCurveMode ? 'exit' : 'activate';
    window.dispatchEvent(new CustomEvent('showCurvePopup', {
      detail: {
        action,
        position: { x: window.innerWidth / 2, y: window.innerHeight / 2 }, // Center of screen
        shapeId: shape.id,
        lineIndex: rectangleIndex
      }
    }));
  }, [shape, isCurveMode, isSelected, rectangleIndex]);

  // Add keyboard event listener for "C" key
  useEffect(() => {
    if (isSelected) {
      document.addEventListener('keydown', handleCurveKeyPress);
      return () => {
        document.removeEventListener('keydown', handleCurveKeyPress);
      };
    }
  }, [isSelected, handleCurveKeyPress]);

  // Handle shared line deletion
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (isSelected && isSharedLine && onDelete && (event.key === 'Delete' || event.key === 'Backspace')) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        console.log('🗑️ Deleting shared line:', shape.id);
        onDelete();
      }
    };

    if (isSelected && isSharedLine && onDelete) {
      // Use capture phase to ensure we get the event first
      document.addEventListener('keydown', handleKeyPress, true);
      return () => {
        document.removeEventListener('keydown', handleKeyPress, true);
      };
    }
  }, [isSelected, isSharedLine, onDelete, shape.id]);

  if (!shape || !shape.nodes) return null;

  const { total } = calculateLineLengths();

  const measurementStyle = new PIXI.TextStyle({ 
    fontSize: 10,
    fill: isBlueprintMode ? 0xffffff : 0x000000,
    fontWeight: 'normal',
    dropShadow: true,
    dropShadowColor: isBlueprintMode ? 0x000000 : 0xffffff,
    dropShadowBlur: 1,
    dropShadowAngle: Math.PI/4,
    dropShadowDistance: 1,
  });

  return (
    <Container
      ref={containerRef}
      x={shape.x}
      y={shape.y}
      interactive={true}
      cursor="pointer"
      sortableChildren={true}
    >
      <Graphics
        draw={drawLine}
        interactive={true}
        cursor="pointer"
        pointerdown={handleMouseDown}
        click={handleMouseDown}
        mousedown={handleMouseDown}
        zIndex={1}
      />

      {/* Control lines (drawn behind control point) */}
      {isCurveMode && isSelected && controlPoint && (
        <Graphics
          draw={drawControlLines}
          interactive={false}
          zIndex={5}
        />
      )}

      {/* Interactive control point */}
      {isCurveMode && isSelected && controlPoint && (
        <Graphics
          x={controlPoint.x}
          y={controlPoint.y}
          draw={drawControlPoint}
          interactive={true}
          cursor="grab"
          pointerdown={(event) => {
            event.stopPropagation();
            handleControlPointMouseDown(event, controlPoint);
          }}
          globalpointermove={(event) => {
            if (isDraggingControl) {
              handleControlPointMouseMove(event);
            }
          }}
          pointerup={handleControlPointMouseUp}
          pointerupoutside={handleControlPointMouseUp}
          zIndex={10}
        />
      )}

      {isSelected && (
        <>
          {console.log('🟢 EditableLine rendering nodes:', {
            isSelected,
            nodeCount: shape.nodes.length,
            shapeId: shape.id,
            isPartOfRectangle,
            nodes: shape.nodes
          })}
          {shape.nodes.map((node, index) => (
            <CornerNode
              key={`node-${index}`}
              x={node.x}
              y={node.y}
              index={index}
              shape={shape}
              onNodeMouseDown={handleNodeMouseDown}
              onNodeMouseMove={handleNodeMouseMove}
              onNodeMouseUp={handleNodeMouseUp}
              containerRef={containerRef}
            />
          ))}
        </>
      )}
      
      <Text
        text={shape.showMeasurement !== false ? `${total}${selectedMeasurementUnit?.abbreviation || 'm'}${isCurveMode ? ' (curved)' : ''}${shape.isGapFiller ? ' (gap)' : ''}` : ''}
        x={(shape.nodes[0].x + shape.nodes[shape.nodes.length - 1].x) / 2 - Math.sin(Math.atan2(
          shape.nodes[shape.nodes.length - 1].y - shape.nodes[0].y,
          shape.nodes[shape.nodes.length - 1].x - shape.nodes[0].x
        )) * (isPartOfRectangle ? 25 : 15) - 15}
        y={(shape.nodes[0].y + shape.nodes[shape.nodes.length - 1].y) / 2 - Math.cos(Math.atan2(
          shape.nodes[shape.nodes.length - 1].y - shape.nodes[0].y,
          shape.nodes[shape.nodes.length - 1].x - shape.nodes[0].x
        )) * (isPartOfRectangle ? 25 : 15) - 4}
        style={measurementStyle}
        zIndex={50}
        visible={shape.showMeasurement !== false}
      />
    </Container>
  );
};

export default EditableLine;
